import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Trash2, Layers } from 'lucide-react'
import { api, ApiError } from '../../api/client'
import { BackButton } from '../../components/ui/BackButton'
import { PageHeader } from '../../components/ui/PageHeader'

const KIND_LABEL_RU: Record<string, string> = {
  '3card': 'Расклад на 3 карты',
  '5card_relationship': 'Расклад на отношения · 5 карт',
  'celtic_cross': 'Кельтский крест · 10 карт',
}

export function TarotHistoryRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const historyQ = useQuery({
    queryKey: ['tarot', 'history'],
    queryFn: () => api.tarotHistory(50),
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.tarotDelete(id),
    onSuccess: () => {
      toast.success(t('tarot_history.deleted'))
      qc.invalidateQueries({ queryKey: ['tarot', 'history'] })
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.body.error.message : (err as Error).message
      toast.error(msg)
    },
    onSettled: () => setDeletingId(null),
  })

  const items = historyQ.data ?? []

  return (
    <div className="flex flex-col gap-4 pb-6">
      <BackButton to="/tarot" />
      <PageHeader title={t('tarot_history.title')} />

      {historyQ.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-sage-100" />
          ))}
        </div>
      )}

      {!historyQ.isLoading && items.length === 0 && (
        <div className="rounded-3xl bg-white/70 p-6 text-center ring-1 ring-sage-200/60">
          <p className="text-sage-700">{t('tarot_history.empty')}</p>
          <button
            type="button"
            onClick={() => navigate('/tarot')}
            className="mt-3 text-sage-900 underline-offset-2 hover:underline"
          >
            {t('tarot_history.open_tarot')}
          </button>
        </div>
      )}

      {items.map((row) => {
        const dt = new Date(row.createdAt)
        return (
          <button
            key={row.id}
            type="button"
            onClick={() => navigate(`/tarot/reading/${row.id}`)}
            className="flex flex-col gap-2 rounded-2xl bg-white/80 p-4 text-left ring-1 ring-sage-200/60 transition hover:ring-sage-400"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-copper" strokeWidth={1.6} aria-hidden />
                <span className="text-sm font-semibold text-sage-900">
                  {KIND_LABEL_RU[row.kind] ?? row.kind}
                </span>
              </div>
              <time className="text-xs text-sage-500" dateTime={row.createdAt}>
                {dt.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
                {' · '}
                {dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </time>
            </div>
            {row.question && (
              <p className="text-xs italic text-sage-600">«{row.question}»</p>
            )}
            <p className="line-clamp-2 text-sm text-sage-800">{row.preview}…</p>
            <div className="flex items-center justify-between pt-1">
              <span className="text-xs text-sage-500">{row.cardCount} карт</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation()
                  if (window.confirm(t('tarot_history.confirm_delete'))) {
                    setDeletingId(row.id)
                    deleteMut.mutate(row.id)
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    if (window.confirm(t('tarot_history.confirm_delete'))) {
                      setDeletingId(row.id)
                      deleteMut.mutate(row.id)
                    }
                  }
                }}
                className={`inline-flex items-center gap-1 text-xs text-sage-500 hover:text-copper ${
                  deletingId === row.id ? 'opacity-50' : ''
                }`}
              >
                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
                {t('tarot_history.delete')}
              </span>
            </div>
          </button>
        )
      })}
    </div>
  )
}
